import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Auth } from '@angular/fire/auth';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ServerResponse } from '../models/server-response.model';

type Params = Record<string, string | number | boolean | null | undefined>;

/**
 * Cliente HTTP único de la consola.
 * - Envía `Authorization: <firebase_id_token>` (sin «Bearer», contrato §0).
 * - Lee la respuesta como texto y convierte a string los enteros de 16+ dígitos
 *   (PK BIGINT de uuid_short) para no perder precisión en JavaScript.
 * - Nunca lanza: los errores vuelven como ServerResponse con su statusCode.
 */
@Injectable({ providedIn: 'root' })
export class ApiService {

  private http = inject(HttpClient);
  private auth = inject(Auth, { optional: true });

  /* -------------------------------------------------------------------------- */
  /*                               ANCHOR token                                  */
  /* -------------------------------------------------------------------------- */
  // region token
  async token(): Promise<string> {
    try {
      return (await this.auth?.currentUser?.getIdToken()) ?? '';
    } catch {
      return '';
    }
  }

  // region get
  get<T>(path: string, params?: Params): Promise<ServerResponse<T>> {
    return this.request<T>('GET', path, undefined, params);
  }

  // region post
  post<T>(path: string, body?: unknown): Promise<ServerResponse<T>> {
    return this.request<T>('POST', path, body ?? {});
  }

  // region put
  put<T>(path: string, body?: unknown): Promise<ServerResponse<T>> {
    return this.request<T>('PUT', path, body ?? {});
  }

  // region delete
  delete<T>(path: string, body?: unknown): Promise<ServerResponse<T>> {
    return this.request<T>('DELETE', path, body);
  }

  /* -------------------------------------------------------------------------- */
  /*                              ANCHOR request                                 */
  /* -------------------------------------------------------------------------- */
  // region request
  async request<T>(method: string, path: string, body?: unknown, params?: Params, baseUrl = environment.urlApi): Promise<ServerResponse<T>> {
    const token = await this.token();
    let httpParams = new HttpParams();
    Object.entries(params ?? {}).forEach(([k, v]) => {
      if (v !== null && v !== undefined && v !== '') httpParams = httpParams.set(k, String(v));
    });

    try {
      const text = await firstValueFrom(
        this.http.request(method, `${baseUrl}${path}`, {
          body: body === undefined ? undefined : JSON.stringify(body),
          params: httpParams,
          responseType: 'text',
          headers: { 'content-type': 'application/json', Authorization: token },
        })
      );
      return new ServerResponse<T>(ApiService.parse(text) ?? { statusCode: 200, message: 'OK', error: '' });
    } catch (error) {
      return ApiService.toErrorResponse<T>(error);
    }
  }

  /* -------------------------------------------------------------------------- */
  /*                              ANCHOR retryOnce                               */
  /* -------------------------------------------------------------------------- */
  // region isDefinitive
  /**
   * 403 (sin acceso) y 404 (no existe) son respuestas definitivas al cargar un recurso. Cualquier otro error
   * (0 = red caída o petición cortada, timeout, 5xx…) puede ser pasajero y NO significa que el recurso no exista.
   */
  static isDefinitive(statusCode: number): boolean {
    return statusCode === 403 || statusCode === 404;
  }

  // region retryOnce
  /**
   * Ejecuta `fn` y, si falla con un error que no es definitivo (ver `isDefinitive`), la vuelve a llamar UNA vez tras
   * `delayMs`. `fn` debe crear una petición nueva en cada llamada.
   */
  static async retryOnce<R extends ServerResponse<unknown> | null>(fn: () => Promise<R>, delayMs = 1000): Promise<R> {
    const res = await fn();
    if (!res || res.ok || ApiService.isDefinitive(res.statusCode)) return res;
    await new Promise(resolve => setTimeout(resolve, delayMs));
    return fn();
  }

  static parse(text: string | null | undefined): any {
    if (!text) return null;
    try {
      // Envuelve en comillas los enteros de 16 o más dígitos (BIGINT) que vienen como número.
      const safe = text.replace(/([:\[,]\s*)(-?\d{16,})(?=\s*[,\]}])/g, '$1"$2"');
      return JSON.parse(safe);
    } catch {
      return null;
    }
  }

  static toErrorResponse<T>(error: unknown): ServerResponse<T> {
    if (error instanceof HttpErrorResponse) {
      const parsed = typeof error.error === 'string' ? ApiService.parse(error.error) : error.error;
      if (parsed && typeof parsed === 'object' && 'message' in parsed) {
        return new ServerResponse<T>({ statusCode: parsed.statusCode ?? error.status, message: parsed.message, error: parsed.error ?? '', data: parsed.data });
      }
      if (parsed && parsed.detail) {
        return new ServerResponse<T>({ statusCode: error.status || 422, message: 'Revisa los datos e intenta de nuevo.', error: parsed.detail });
      }
      const message = error.status === 0 ? 'Sin conexión. Revisa tu internet e intenta de nuevo.' : 'Algo salió mal. Intenta de nuevo.';
      return new ServerResponse<T>({ statusCode: error.status || 0, message, error: error.message });
    }
    return new ServerResponse<T>({ statusCode: 0, message: 'Algo salió mal. Intenta de nuevo.', error: String(error) });
  }
}
